<template>
  <b-table-simple
    bordered
    small
    :responsive="false"
    class="mapping-table"
    :class="{ 'mapping-table-wide': !compact, 'mapping-table--compact': compact }"
  >
    <b-thead head-variant="light">
      <b-tr>
        <b-th v-if="showSeqColumn" class="text-center col-seq-th">#</b-th>
        <b-th>
          <div class="column-name-header">
            <b-form-checkbox
              v-if="rows.length"
              :checked="allIncluded"
              :indeterminate="includeIndeterminate"
              class="column-include-cb column-include-cb--header mb-0"
              aria-label="Include all columns"
              @change="onSetAllInclude"
            />
            <MappingSortableHeader
              field="columnName"
              :sort-state="sortState"
              @sort-toggle="onSortToggle"
            >
              Column name
            </MappingSortableHeader>
          </div>
        </b-th>
        <b-th v-if="showFilterColumn" class="text-center">Filter</b-th>
        <b-th class="col-display-name-th">
          <MappingSortableHeader
            field="displayName"
            :sort-state="sortState"
            @sort-toggle="onSortToggle"
          >
            Display name
          </MappingSortableHeader>
        </b-th>
        <b-th class="col-display-type-th">Type</b-th>
        <b-th class="col-aggregate-th">Aggregate</b-th>
        <b-th v-if="showGraphColumn">Graph</b-th>
      </b-tr>
    </b-thead>
    <b-tbody>
      <b-tr v-if="!rows.length">
        <b-td :colspan="tableColspan" class="text-center text-muted small py-3">
          <slot name="empty">No columns loaded.</slot>
        </b-td>
      </b-tr>
      <b-tr
        v-for="row in rows"
        :key="'tblcol-' + row.columnName"
      >
        <b-td v-if="showSeqColumn" class="text-center align-middle col-seq-cell">
          {{ row.seq }}
        </b-td>
        <b-td class="align-middle col-name-cell">
          <div class="column-name-cell">
            <b-form-checkbox
              :checked="row.include"
              class="column-include-cb mb-0"
              :aria-label="'Include ' + row.columnName"
              @change="(v) => onIncludeChange(row, v)"
            />
            <code
              class="column-name-readonly column-name-readonly--inline"
              :title="row.columnName"
              >{{ row.columnName }}</code
            >
          </div>
        </b-td>
        <b-td v-if="showFilterColumn" class="text-center align-middle">
          <b-form-checkbox
            :checked="row.filterable"
            size="sm"
            class="mb-0"
            @change="(v) => onFieldChange(row, 'filterable', v)"
          />
        </b-td>
        <b-td class="align-middle col-display-name-cell">
          <input
            :value="row.displayName"
            class="inline-mapping-input"
            :title="'Label for ' + row.columnName"
            @input="(e) => onFieldChange(row, 'displayName', e.target.value)"
          />
        </b-td>
        <b-td class="align-middle col-display-type-cell">
          <b-form-select
            :value="row.displayType"
            :options="displayTypeOptions"
            size="sm"
            class="type-select"
            @change="(v) => onDisplayTypeChange(row, v)"
          />
        </b-td>
        <b-td
          class="align-middle col-aggregate-cell aggregate-cell"
          :class="{ 'aggregate-cell--inactive': !aggregateEnabled(row) }"
        >
          <b-form-select
            :value="row.aggregate"
            :options="aggregateOptions(row)"
            size="sm"
            class="type-select"
            :disabled="!aggregateEnabled(row)"
            @change="(v) => onFieldChange(row, 'aggregate', v)"
          />
        </b-td>
        <b-td
          v-if="showGraphColumn"
          class="align-middle graph-cell"
          :class="{ 'graph-cell--inactive': !graphEnabled(row) }"
        >
          <b-form-select
            :value="row.graph"
            :options="graphOptions"
            size="sm"
            class="type-select"
            :disabled="!graphEnabled(row)"
            @change="(v) => onFieldChange(row, 'graph', v)"
          />
        </b-td>
      </b-tr>
    </b-tbody>
  </b-table-simple>
</template>

<script>
import { TABLE_MODE_DISPLAY_TYPE_OPTIONS, TABLE_BAR_GRAPH_OPTIONS } from "@/common/columnMappingConstants";
import {
  aggregateSelectEnabledForRow,
  aggregateOptionsForRow,
  tableModeGraphSelectEnabledForRow,
  onTableColumnDisplayTypeChange,
} from "@/common/columnMappingAggregate";
import MappingSortableHeader from "./MappingSortableHeader.vue";

export default {
  name: "TableVariableMappingTable",
  components: { MappingSortableHeader },
  props: {
    rows: { type: Array, default: () => [] },
    showGraphColumn: { type: Boolean, default: true },
    showFilterColumn: { type: Boolean, default: true },
    /** Hidden in VizletHeader; shown in menu-config admin */
    showSeqColumn: { type: Boolean, default: true },
    sortState: { type: Array, default: () => [] },
    /** Vizlet drawer: tighter layout, no horizontal table min-width */
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      displayTypeOptions: TABLE_MODE_DISPLAY_TYPE_OPTIONS,
      graphOptions: TABLE_BAR_GRAPH_OPTIONS,
    };
  },
  computed: {
    tableColspan() {
      let n = 4;
      if (this.showSeqColumn) n += 1;
      if (this.showFilterColumn) n += 1;
      if (this.showGraphColumn) n += 1;
      return n;
    },
    allIncluded() {
      const r = this.rows || [];
      return r.length > 0 && r.every((row) => row && row.include);
    },
    includeIndeterminate() {
      const r = this.rows || [];
      if (!r.length) return false;
      const n = r.filter((row) => row && row.include).length;
      return n > 0 && n < r.length;
    },
  },
  methods: {
    aggregateEnabled(row) {
      return aggregateSelectEnabledForRow(row);
    },
    aggregateOptions(row) {
      return aggregateOptionsForRow(row);
    },
    graphEnabled(row) {
      return tableModeGraphSelectEnabledForRow(row);
    },
    onSetAllInclude(include) {
      this.$emit("set-all-include", !!include);
    },
    onIncludeChange(row, include) {
      this.$emit("row-change", { row, field: "include", value: !!include });
    },
    onDisplayTypeChange(row, displayType) {
      const next = { ...row, displayType };
      onTableColumnDisplayTypeChange(next);
      this.$emit("row-change", {
        row,
        field: "displayType",
        value: displayType,
        rowPatch: {
          displayType: next.displayType,
          aggregate: next.aggregate,
        },
      });
    },
    onFieldChange(row, field, value) {
      this.$emit("row-change", { row, field, value });
    },
    onSortToggle(field) {
      this.$emit("sort-toggle", field);
    },
  },
};
</script>
